import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CONTACT } from "@/lib/constants";
import { apiFetch } from "@/lib/api";
import { Star, MessageCircle, Send } from "lucide-react";

interface Review {
  id: string;
  authorName: string;
  rating: number;
  content: string;
  serviceType: string;
  createdAt: string;
  title?: string;
  thumbnailUrl?: string;
  publishedAt?: string;
}

/* @section: reviews */
export default function ReviewsPage() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/reviews")
      .then((r) => r.json())
      .then((d) => {
        const raw: Review[] = d.data ?? [];
        raw.sort((a, b) => {
          const da = new Date((a.publishedAt && a.publishedAt.trim()) ? a.publishedAt : a.createdAt).getTime();
          const db = new Date((b.publishedAt && b.publishedAt.trim()) ? b.publishedAt : b.createdAt).getTime();
          return db - da;
        });
        setReviews(raw); setLoading(false);
      })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  return (
    <div className="min-h-screen bg-[#2E8B57] font-sans">
      <Navbar />

      {/* Header */}
      <div className="pt-16 bg-[#2E8B57] pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center py-16">
          <div className="w-12 h-1 bg-[#FFE000] mx-auto mb-4" />
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">客戶評價</h1>
          <p className="text-gray-200 text-lg">真實客戶分享，見證每次養生保健的效果</p>
          {reviews.length > 0 && (
            <div className="mt-6 inline-flex items-center gap-3 bg-white/10 rounded-full px-6 py-3">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={18} className={i <= Math.round(parseFloat(avgRating)) ? "text-[#FFE000] fill-[#FFE000]" : "text-gray-400"} />
                ))}
              </div>
              <span className="text-white font-bold text-xl">{avgRating}</span>
              <span className="text-gray-300 text-sm">/ {reviews.length} 個評價</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white/10 rounded-2xl h-64 animate-pulse" />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-6xl mb-4">⭐</p>
            <h2 className="text-2xl font-bold text-white mb-3">即將推出</h2>
            <p className="text-gray-200 mb-6">敬請期待客戶評價！</p>
            <div className="flex gap-3 justify-center">
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white rounded-full text-sm font-medium">
                <MessageCircle size={15} /> 立即體驗
              </a>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <article key={r.id} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col">
                {/* Thumbnail image */}
                {r.thumbnailUrl ? (
                  <div className="w-full h-48 overflow-hidden flex-shrink-0">
                    <img src={r.thumbnailUrl} alt={r.title || r.authorName} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  /* Fallback decorative header */
                  <div className="w-full h-16 bg-gradient-to-r from-[#2E8B57] to-[#1C6034] flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl text-[#FFE000]">{"★".repeat(r.rating)}</span>
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  {/* Title */}
                  <h3 className="font-bold text-[#0A1628] text-base mb-2 leading-snug">
                    {r.title || (r.authorName ? `${r.authorName}的評價` : '客戶評價')}
                  </h3>

                  {/* Author / rating / service row */}
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <div className="w-7 h-7 rounded-full bg-[#1C6034] flex items-center justify-center text-[#FFE000] font-bold text-sm flex-shrink-0">
                      {(r.authorName || '匿').charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{r.authorName || "匿名客戶"}</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={12} className={i <= r.rating ? "text-[#FFE000] fill-[#FFE000]" : "text-gray-200 fill-gray-200"} />
                      ))}
                    </div>
                    {r.serviceType && (
                      <span className="text-xs bg-[#2E8B57] text-white px-2 py-0.5 rounded-full">
                        {r.serviceType}
                      </span>
                    )}
                  </div>

                  {/* Rich content */}
                  <div
                    className="text-gray-600 text-sm leading-relaxed flex-1 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: r.content }}
                  />

                  {/* Date */}
                  <p className="text-gray-400 text-xs mt-4 pt-3 border-t border-gray-100">
                    {new Date((r.publishedAt && r.publishedAt.trim()) ? r.publishedAt : r.createdAt).toLocaleDateString("zh-HK")}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-[#1C6034] rounded-2xl p-8 text-center">
          <h3 className="text-white font-bold text-2xl mb-3">立即預約，親身體驗</h3>
          <p className="text-gray-300 mb-6">加入我們的滿意客戶行列，感受深層放鬆的養生之旅</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#1ebe5b] transition-colors">
              <MessageCircle size={17} /> WhatsApp 預約
            </a>
            <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center gap-2 px-6 py-3 bg-[#2AABEE] text-white font-semibold rounded-full hover:bg-[#1a9fd4] transition-colors">
              <Send size={17} /> Telegram 預約
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
